import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronDown, Plus } from 'lucide-react';

export default function Autocomplete({ items = [], value = '', onChange, onSelect, placeholder, displayField = 'name', secondaryField, showCreateNew = true }) {
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (open) {
      if (value && value.length > 0) {
        const q = norm(value);
        const f = items.filter(item =>
          norm(item[displayField]).includes(q) ||
          (secondaryField && norm(item[secondaryField]).includes(q))
        );
        setFiltered(f.slice(0, 10));
      } else {
        setFiltered(items.slice(0, 10));
      }
    }
  }, [value, items, displayField, secondaryField, open]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasExactMatch = filtered.some(i => (i[displayField] || '').toLowerCase() === (value || '').toLowerCase());

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Input value={value} onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl pr-8" />
        <button type="button" onClick={() => setOpen(!open)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50">
          <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-[#0A1F1A] border border-white/10 rounded-xl shadow-lg max-h-52 overflow-auto">
          {filtered.map((item, i) => (
            <div key={item.id || i} className="px-3 py-2.5 hover:bg-[#A38560]/10 cursor-pointer text-sm text-white transition-colors border-b border-white/5 last:border-0"
              onClick={() => { onSelect(item); setOpen(false); }}>
              <span>{item[displayField]}</span>
              {secondaryField && item[secondaryField] && (
                <span className="text-xs text-white/30 ml-2">{item[secondaryField]}</span>
              )}
            </div>
          ))}
          {filtered.length === 0 && value && (
            <div className="px-3 py-3 text-xs text-white/30 text-center">Sin coincidencias</div>
          )}
          {showCreateNew && value && value.trim().length > 0 && !hasExactMatch && (
            <div className="px-3 py-2.5 hover:bg-green-500/10 cursor-pointer text-sm text-green-400 transition-colors flex items-center gap-2 border-t border-white/5"
              onClick={() => { setOpen(false); }}>
              <Plus size={14} /> Crear nuevo: "{value.trim()}"
            </div>
          )}
          {filtered.length === 0 && !value && (
            <div className="px-3 py-3 text-xs text-white/30 text-center">Escribe para buscar...</div>
          )}
        </div>
      )}
    </div>
  );
}
